import type { FieldDef } from './primitives/types';
import {
  formatDecimalFixed,
  formatDecimalNatural,
  formatPercentageFixed,
  formatPercentageNatural,
  parseDateLocal,
} from '@packages/core';

const numberFormatter = new Intl.NumberFormat('en-US');

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

function parseDate(value: unknown): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const date = parseDateLocal(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function formatUsDate(date: Date): string {
  return `${pad2(date.getMonth() + 1)}/${pad2(date.getDate())}/${date.getFullYear()}`;
}

function formatUsDateTime(date: Date): string {
  let hours = date.getHours();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return `${formatUsDate(date)} ${hours}:${pad2(date.getMinutes())} ${ampm}`;
}

function getTimezoneAbbr(): string {
  const str = new Date().toString();
  const match = str.match(/\(([^)]+)\)$/);
  if (match) {
    const letters = match[1].match(/[A-Z]/g);
    if (letters) return letters.join('');
  }
  const offset = new Date().getTimezoneOffset();
  const sign = offset <= 0 ? '+' : '-';
  const absH = Math.floor(Math.abs(offset) / 60);
  return `GMT${sign}${absH}`;
}

function formatUsDateTimeTz(date: Date): string {
  return `${formatUsDateTime(date)} ${getTimezoneAbbr()}`;
}

function toNumber(value: unknown): number {
  if (typeof value === 'number') return value;
  return Number(String(value).replace(/,/g, '').replace(/%$/, ''));
}

function formatCurrency(value: unknown, precision?: number, naturalPrecision = false): string {
  const num = toNumber(value);
  if (Number.isNaN(num)) return String(value);
  if (num === 0) return '$0';
  const maxPrecision = precision ?? 2;
  const formatted = naturalPrecision
    ? formatDecimalNatural(num, maxPrecision)
    : formatDecimalFixed(num, maxPrecision);
  return `$${formatted}`;
}

function formatNumber(value: unknown, precision?: number, naturalPrecision = false): string {
  const num = toNumber(value);
  if (Number.isNaN(num)) return String(value);
  if (precision != null) {
    return naturalPrecision
      ? formatDecimalNatural(num, precision)
      : formatDecimalFixed(num, precision);
  }
  return numberFormatter.format(num);
}

function formatPercentage(value: unknown, precision?: number, naturalPrecision = false): string {
  const num = toNumber(value);
  if (Number.isNaN(num)) return `${String(value)}%`;
  if (precision != null) {
    return naturalPrecision
      ? formatPercentageNatural(num, precision)
      : formatPercentageFixed(num, precision);
  }
  return `${num}%`;
}

function maskSsnDisplay(value: unknown): string {
  const digits = String(value).replace(/\D/g, '');
  const last4 = digits.slice(-4);
  if (last4.length === 0) return '***-**-****';
  return `***-**-${last4}`;
}

function formatFileName(value: unknown): string {
  const str = String(value);
  const parts = str.split(/[/\\]/);
  return parts[parts.length - 1] || str;
}

function formatBoolean(value: unknown): string {
  if (value === true || value === 'true' || value === 1 || value === '1') return 'Yes';
  if (value === false || value === 'false' || value === 0 || value === '0') return 'No';
  return String(value);
}

function extractObjectLines(obj: unknown, keys?: readonly string[]): string[] {
  if (obj == null || typeof obj !== 'object' || Array.isArray(obj)) return [];
  const record = obj as Record<string, unknown>;
  const orderedKeys = keys ?? Object.keys(record);
  return orderedKeys
    .map((k) => record[k])
    .filter((v) => v != null && v !== '' && v !== false && typeof v !== 'object')
    .map(String);
}

export function formatMultiline(value: unknown, keys?: readonly string[]): string {
  return extractObjectLines(value, keys).join('\n');
}

export function formatMultilineList(value: unknown, keys?: readonly string[]): string {
  if (!Array.isArray(value) || value.length === 0) return '';
  return value
    .map((item) => extractObjectLines(item, keys).join('\n'))
    .filter((block) => block !== '')
    .join('\n\n');
}

export function formatForDisplay(
  value: unknown,
  def: FieldDef,
  options?: { naturalPrecision?: boolean },
): string {
  if (isEmpty(value)) {
    return def.emptyText ?? '-';
  }

  const precision = def.displayPrecision;
  const naturalPrecision = options?.naturalPrecision ?? false;

  switch (def.displayFormat) {
    case 'text':
      return String(value);
    case 'currency':
      return formatCurrency(value, precision, naturalPrecision);
    case 'date': {
      const date = parseDate(value);
      return date ? formatUsDate(date) : String(value);
    }
    case 'datetime': {
      const date = parseDate(value);
      return date ? formatUsDateTime(date) : String(value);
    }
    case 'datetime-tz': {
      const date = parseDate(value);
      return date ? formatUsDateTimeTz(date) : String(value);
    }
    case 'number':
      return formatNumber(value, precision, naturalPrecision);
    case 'percentage':
      return formatPercentage(value, precision, naturalPrecision);
    case 'phone':
      return String(value);
    case 'ssn':
      return maskSsnDisplay(value);
    case 'masked':
      return '••••••';
    case 'html':
      return String(value);
    case 'boolean':
      return formatBoolean(value);
    case 'file':
      return formatFileName(value);
    case 'action-link':
      return formatCurrency(value, precision, naturalPrecision);
    case 'status':
      return String(value);
    case 'badge':
      return String(value);
    case 'multiline':
      return typeof value === 'object' && value != null ? formatMultiline(value) : '';
    case 'multiline-list':
      return Array.isArray(value) ? formatMultilineList(value) : '';
    default:
      return String(value);
  }
}

/**
 * Formats a date range from two values into 'MM/DD/YYYY - MM/DD/YYYY'.
 * If endDate is null/empty, renders as 'MM/DD/YYYY -'.
 * Returns null if startDate is null/empty.
 */
export function formatDateRange(startDate: unknown, endDate: unknown): string | null {
  if (isEmpty(startDate)) return null;
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  if (start == null) return null;
  return `${formatUsDate(start)} - ${end != null ? formatUsDate(end) : ''}`;
}
